/**
 * Group serial inventory rows by definition (itemDefinitionId + isOrganic),
 * with fallback to trimmed itemName + isOrganic when definition id is missing.
 * Non-serial rows are returned unchanged, then synthetic aggregate rows are appended.
 */

function hasSerialNumber(item) {
  return !!(item.serialNumber && String(item.serialNumber).trim() !== "");
}

/** Same key used for grouped rows and for detail-mode “all serials of this item” distribution. */
export function getSerialInventoryGroupKey(item) {
  const organic = !!item.isOrganic;
  const defId =
    item.itemDefinitionId != null && String(item.itemDefinitionId).trim() !== ""
      ? String(item.itemDefinitionId).trim()
      : "";
  if (defId) {
    return `def:${defId}:${organic ? "1" : "0"}`;
  }
  const name = String(item.itemName || "").trim().toLowerCase();
  return `name:${name}:${organic ? "1" : "0"}`;
}

function sumNum(...vals) {
  let s = 0;
  for (const v of vals) {
    const n = Number(v);
    if (!Number.isNaN(n)) s += n;
  }
  return s;
}

function deriveGroupStatus(members) {
  let anyReturned = false;
  let anyAssigned = false;
  let anyAvailable = false;
  for (const m of members) {
    const st = m.calculatedStatus || m.status || "available";
    if (st === "returned") anyReturned = true;
    else if (st === "assigned") anyAssigned = true;
    else if (st === "available") anyAvailable = true;
  }
  if (anyReturned) return "returned";
  if (anyAssigned) return "assigned";
  if (anyAvailable) return "available";
  return "available";
}

/**
 * @param {Array<Object>} filteredInventory - enhanced inventory rows
 * @returns {Array<Object>} detail order preserved for non-serial; serials replaced by one row per group
 */
export function buildSerialGroupedInventoryRows(filteredInventory) {
  if (!filteredInventory?.length) return [];

  const nonSerial = [];
  const serialByKey = new Map();

  for (const item of filteredInventory) {
    if (!hasSerialNumber(item)) {
      nonSerial.push(item);
      continue;
    }
    const key = getSerialInventoryGroupKey(item);
    let bucket = serialByKey.get(key);
    if (!bucket) {
      bucket = [];
      serialByKey.set(key, bucket);
    }
    bucket.push(item);
  }

  const groupRows = [];
  for (const [key, members] of serialByKey) {
    members.sort((a, b) =>
      String(a.serialNumber || "").localeCompare(
        String(b.serialNumber || ""),
        "he",
      ),
    );
    const rep = members[0];
    const n = members.length;

    let serialGroupAvailableCount = 0;
    let serialGroupAssignedCount = 0;
    let serialGroupReturnedCount = 0;
    for (const m of members) {
      const st = m.calculatedStatus || m.status || "available";
      if (st === "returned") serialGroupReturnedCount++;
      else if (st === "assigned") serialGroupAssignedCount++;
      else serialGroupAvailableCount++;
    }
    const serialGroupStatusSummary = [
      `${serialGroupAvailableCount} זמינים`,
      `${serialGroupAssignedCount} מוחתמים`,
      ...(serialGroupReturnedCount > 0
        ? [`${serialGroupReturnedCount} זוכו`]
        : []),
    ].join(" · ");

    const quantity = sumNum(
      ...members.map((m) => m.displayQuantity ?? m.quantity ?? 0),
    );
    const assignedQuantity = sumNum(
      ...members.map((m) => m.assignedQuantity ?? 0),
    );
    const availableQuantity = sumNum(
      ...members.map((m) => m.availableQuantity ?? 0),
    );

    const calculatedStatus = deriveGroupStatus(members);

    groupRows.push({
      ...rep,
      id: `serial-group:${key}`,
      isSerialGroupRow: true,
      serialGroupMemberIds: members.map((m) => m.id),
      /** Empty so row logic treats group like non-serial; show count via serialGroupSerialCount */
      serialNumber: "",
      serialGroupSerialCount: n,
      serialGroupAvailableCount,
      serialGroupAssignedCount,
      serialGroupReturnedCount,
      serialGroupStatusSummary,
      quantity,
      displayQuantity: quantity,
      assignedQuantity,
      availableQuantity,
      calculatedStatus,
      status: calculatedStatus,
      assignedPersonnel: [],
      seenAt: null,
      assignments: rep.assignments,
      itemName: rep.itemName,
      itemDefinitionId: rep.itemDefinitionId,
      isOrganic: rep.isOrganic,
      currentUnitId: rep.currentUnitId,
    });
  }

  const combined = [...nonSerial, ...groupRows];
  combined.sort((a, b) =>
    String(a.itemName || "").localeCompare(String(b.itemName || ""), "he"),
  );
  return combined;
}
